/**
 * IndexedDB availability detection utility
 * Detects if IndexedDB is available and functional in the current browser environment
 * Handles edge cases like Safari private mode, Firefox private browsing, and storage restrictions
 */

import logger from './logger';

/**
 * Check if IndexedDB is available and functional
 * @returns {Promise<{available: boolean, reason?: string}>}
 */
export async function checkIndexedDBAvailability() {
  logger.group('IndexedDB Availability Check');
  logger.time('indexeddb-check');

  try {
    // Step 1: Check for IndexedDB API presence
    if (!window.indexedDB) {
      logger.error('IndexedDB API not found in window object');
      logger.groupEnd();
      return {
        available: false,
        reason: 'IndexedDB is not supported in this browser',
      };
    }
    logger.debug('IndexedDB API is present');

    // Step 2: Check for private browsing mode (various browsers)
    const isPrivateMode = await detectPrivateBrowsing();
    if (isPrivateMode) {
      logger.warn('Private browsing mode detected - IndexedDB may be restricted');
      // Don't immediately fail, as some browsers allow IndexedDB in private mode
    }

    // Step 3: Attempt to open a test database
    const testDbName = 'ti-m-indexeddb-test-' + Date.now();
    logger.debug(`Attempting to open test database: ${testDbName}`);

    try {
      const openRequest = window.indexedDB.open(testDbName, 1);

      const result = await new Promise((resolve, reject) => {
        let timeoutId;

        // Set a timeout for the operation
        timeoutId = setTimeout(() => {
          logger.error('IndexedDB open request timed out after 5 seconds');
          reject(new Error('IndexedDB open request timed out'));
        }, 5000);

        openRequest.onsuccess = () => {
          clearTimeout(timeoutId);
          logger.debug('Test database opened successfully');
          resolve(true);
        };

        openRequest.onerror = () => {
          clearTimeout(timeoutId);
          logger.error('Failed to open test database:', openRequest.error);
          reject(openRequest.error);
        };

        openRequest.onblocked = () => {
          clearTimeout(timeoutId);
          logger.error('Test database open request was blocked');
          reject(new Error('Database open request was blocked'));
        };

        openRequest.onupgradeneeded = (event) => {
          logger.debug('Database upgrade needed - creating object store');
          const db = event.target.result;
          if (!db.objectStoreNames.contains('test')) {
            db.createObjectStore('test', { keyPath: 'id' });
          }
        };
      });

      if (!result) {
        throw new Error('Database open did not complete successfully');
      }

      // Step 4: Test write operation
      const db = openRequest.result;
      logger.debug('Testing write operation to database');

      const writeTest = await new Promise((resolve, reject) => {
        try {
          const transaction = db.transaction(['test'], 'readwrite');
          const store = transaction.objectStore('test');
          const request = store.add({ id: 1, test: 'data' });

          request.onsuccess = () => {
            logger.debug('Write test successful');
            resolve(true);
          };

          request.onerror = () => {
            logger.error('Write test failed:', request.error);
            reject(request.error);
          };

          transaction.onerror = () => {
            logger.error('Transaction failed:', transaction.error);
            reject(transaction.error);
          };
        } catch (error) {
          logger.error('Error creating transaction:', error);
          reject(error);
        }
      });

      // Step 5: Clean up test database
      db.close();
      const deleteRequest = window.indexedDB.deleteDatabase(testDbName);

      await new Promise((resolve) => {
        deleteRequest.onsuccess = () => {
          logger.debug('Test database deleted successfully');
          resolve();
        };
        deleteRequest.onerror = () => {
          logger.warn('Failed to delete test database (non-critical)');
          resolve(); // Don't fail the whole check because of cleanup
        };
      });

      logger.timeEnd('indexeddb-check');
      logger.info('IndexedDB is fully available and functional');
      logger.groupEnd();

      return {
        available: true,
      };

    } catch (error) {
      logger.error('IndexedDB functionality test failed:', error);

      // Determine specific reason for failure
      let reason = 'IndexedDB is not functional: ';

      if (error.name === 'SecurityError' || error.name === 'NotAllowedError') {
        reason += 'Browser security settings prevent IndexedDB usage';
      } else if (error.name === 'QuotaExceededError') {
        reason += 'Storage quota exceeded';
      } else if (error.message?.includes('blocked')) {
        reason += 'Database access is blocked';
      } else if (error.message?.includes('timeout')) {
        reason += 'Database operations are timing out';
      } else {
        reason += error.message || 'Unknown error';
      }

      logger.timeEnd('indexeddb-check');
      logger.groupEnd();

      return {
        available: false,
        reason,
      };
    }

  } catch (error) {
    logger.error('Unexpected error during IndexedDB check:', error);
    logger.timeEnd('indexeddb-check');
    logger.groupEnd();

    return {
      available: false,
      reason: `Unexpected error: ${error.message}`,
    };
  }
}

/**
 * Detect if the browser is in private/incognito mode
 * @returns {Promise<boolean>}
 */
async function detectPrivateBrowsing() {
  try {
    // Firefox
    if ('MozAppearance' in document.documentElement.style) {
      if (window.indexedDB && /Firefox/.test(navigator.userAgent)) {
        const db = window.indexedDB.open('test');
        await new Promise((resolve) => {
          db.onerror = () => {
            logger.debug('Firefox private browsing detected');
            resolve(true);
          };
          db.onsuccess = () => {
            db.result.close();
            window.indexedDB.deleteDatabase('test');
            resolve(false);
          };
        });
      }
    }

    // Safari
    if (navigator.vendor && navigator.vendor.indexOf('Apple') > -1) {
      try {
        window.openDatabase(null, null, null, null);
        return false;
      } catch (e) {
        logger.debug('Safari private browsing detected');
        return true;
      }
    }

    // Chrome/Edge incognito - check for quota
    if (window.webkitRequestFileSystem) {
      return new Promise((resolve) => {
        window.webkitRequestFileSystem(
          window.TEMPORARY,
          1,
          () => resolve(false),
          () => {
            logger.debug('Chrome/Edge incognito mode detected');
            resolve(true);
          },
        );
      });
    }

    // Check storage.estimate() API
    if (navigator.storage && navigator.storage.estimate) {
      const { usage, quota } = await navigator.storage.estimate();
      if (quota < 120000000) {
        // Less than 120MB usually indicates private mode
        logger.debug('Private mode detected via storage quota');
        return true;
      }
    }

    return false;
  } catch (error) {
    logger.warn('Error detecting private browsing:', error);
    return false;
  }
}

/**
 * Get storage quota information
 * @returns {Promise<{usage: number, quota: number, percentUsed: number}>}
 */
export async function getStorageQuota() {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      const percentUsed = (estimate.usage / estimate.quota) * 100;

      logger.info(`Storage usage: ${(estimate.usage / 1024 / 1024).toFixed(2)}MB / ${(estimate.quota / 1024 / 1024).toFixed(2)}MB (${percentUsed.toFixed(2)}%)`);

      return {
        usage: estimate.usage,
        quota: estimate.quota,
        percentUsed,
      };
    }
  } catch (error) {
    logger.error('Error getting storage quota:', error);
  }

  return {
    usage: 0,
    quota: 0,
    percentUsed: 0,
  };
}

/**
 * Request persistent storage (Chrome/Edge)
 * @returns {Promise<boolean>}
 */
export async function requestPersistentStorage() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      const isPersisted = await navigator.storage.persisted();
      if (!isPersisted) {
        logger.info('Requesting persistent storage permission');
        const granted = await navigator.storage.persist();
        if (granted) {
          logger.info('Persistent storage granted');
          return true;
        } else {
          logger.warn('Persistent storage denied');
          return false;
        }
      } else {
        logger.info('Storage is already persistent');
        return true;
      }
    }
  } catch (error) {
    logger.error('Error requesting persistent storage:', error);
  }
  return false;
}